// components/DocumentTable.tsx
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Table, Badge, Button } from "react-bootstrap";
import {
    FileText,
    Calendar,
    ExternalLink,
    Trash2,
} from "lucide-react";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { LogError } from "../../../../../utils/GlobalLogging";
import SimpleLoader from "../../../apps/components/SimpleLoader";
import DeleteConfirmationModal from "../../../apps/components/DeleteConfirmationModal";
import { IProjectDocument } from "../../../core/_models";
import { CustomDateFormate } from "../../../../../utils/date";
import { useDocumentCategoryQuery } from "../../core/_queries";
import {
    deleteProjectPETDocument,
    getProjectPETDocuments,
    uploadProjectPETDocuments,
} from "../core/_request";
import { getApiErrorMessage } from "../../../../../utils/utils";
import { isAbortedError } from "../../../../../utils/global";

const AddDocument = lazy(() => import("../../components/AddDocument"));

export const DocumentTable = ({ projectId }: { projectId: number }) => {
    const { showSnackbar } = useSnackbar();
    const { categories, isLoading: isCategoriesLoading } = useDocumentCategoryQuery();
    const [document, setDocument] = useState<IProjectDocument[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchProjectDocuments = useCallback(async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            setIsLoading(true);

            const response = await getProjectPETDocuments(
                projectId,
                controller.signal
            );

            if (controller.signal.aborted) return;

            if (response.status === "ok") {
                setDocument(response.data.project_docs ?? []);
            } else {
                showSnackbar(
                    response.message || "Failed to fetch Project Documents",
                    "danger"
                );
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            LogError("Error fetching:", error, "getProjectPETDocuments");
            showSnackbar(getApiErrorMessage(error, "Failed to fetch Project Documents"), "danger");
        } finally {
            if (!controller.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [projectId, showSnackbar]);

    useEffect(() => {
        fetchProjectDocuments();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [fetchProjectDocuments]);

    const handleAddDocument = useCallback(() => {
        setShowModal(true);
    }, []);

    const handleSave = useCallback(
        async (formData: IProjectDocument) => {
            setShowModal(false);

            try {
                setIsLoading(true);

                const response = await uploadProjectPETDocuments({
                    project_id: projectId,
                    documents: [{ ...formData, doc_id: 0 }],
                });

                if (response.status === "ok") {
                    showSnackbar(
                        response.message || "Document uploaded successfully",
                        "success"
                    );
                    setDocument(response.data.project_docs ?? []);

                    return;
                }

                showSnackbar(
                    response.message || "Failed to upload document",
                    "danger"
                );
            } catch (error) {
                LogError(
                    "Error uploading:",
                    error,
                    "uploadProjectPETDocuments"
                );
                showSnackbar(getApiErrorMessage(error, "Failed to upload document"), "danger");
            } finally {
                setIsLoading(false);
            }
        },
        [projectId, showSnackbar]
    );

    const handleDeleteClick = useCallback((doc: IProjectDocument) => {
        if (!doc.can_delete || !doc.doc_id) {
            return;
        }

        setSelectedDocId(doc.doc_id);
        setShowDeleteModal(true);
    }, []);

    const handleCloseDeleteModal = useCallback(() => {
        if (isDeleting) {
            return;
        }

        setShowDeleteModal(false);
        setSelectedDocId(null);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(async () => {
        if (!selectedDocId) {
            return;
        }

        try {
            setIsDeleting(true);

            const response = await deleteProjectPETDocument({
                project_id: projectId,
                doc_id: selectedDocId,
            });

            if (response.status === "ok") {
                showSnackbar(
                    response.message || "Document deleted successfully",
                    "success"
                );
                setDocument(response.data.project_docs ?? []);
                setShowDeleteModal(false);
                setSelectedDocId(null);
                return;
            }

            showSnackbar(
                response.message || "Failed to delete document",
                "danger"
            );
        } catch (error) {
            LogError("Error deleting:", error, "deleteProjectPETDocument");
            showSnackbar(getApiErrorMessage(error, "Failed to delete document"), "danger");
        } finally {
            setIsDeleting(false);
        }
    }, [projectId, selectedDocId, showSnackbar]);

    const showLoading = isLoading || isCategoriesLoading;

    return (
        <div className="card p-4 shadow-sm">
            <div className="document-table">
                {showLoading && <SimpleLoader />}
                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h5 className="fw-bold mb-1">Documents</h5>
                        <p className="text-muted fs-7 mb-0">
                            {document.length} documents found
                        </p>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleAddDocument}
                        disabled={showLoading || isDeleting}
                    >
                        Add Document
                    </Button>
                </div>

                {/* Table */}
                <div
                    className="table-responsive border rounded-3"
                    style={{ maxHeight: "55vh", overflowY: "auto" }}
                >
                    <Table hover className="align-middle mb-0">
                        <thead
                            className="bg-light position-sticky top-0"
                            style={{ zIndex: 1 }}
                        >
                            <tr>
                                <th
                                    className="text-center fw-semibold text-gray-700 p-3"
                                    style={{ width: "70px" }}
                                >
                                    #
                                </th>

                                <th
                                    className="fw-semibold text-gray-700 p-3"
                                    style={{ minWidth: "360px" }}
                                >
                                    Document Name
                                </th>

                                <th
                                    className="fw-semibold text-gray-700 p-3"
                                >
                                    Stage
                                </th>

                                <th
                                    className="text-center fw-semibold text-gray-700 p-3"
                                    style={{ width: "110px" }}
                                >
                                    Type
                                </th>

                                <th
                                    className="text-center fw-semibold text-gray-700 p-3"
                                    style={{ width: "150px" }}
                                >
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                        <Calendar size={14} />
                                        <span>Date</span>
                                    </div>
                                </th>

                                <th
                                    className="text-center fw-semibold text-gray-700 p-3"
                                    style={{ width: "110px" }}
                                >
                                    Action
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {document.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-5">
                                        <div className="d-flex flex-column align-items-center gap-2">
                                            <FileText
                                                size={48}
                                                className="text-muted opacity-25"
                                            />

                                            <p className="text-muted mb-0">
                                                No documents found
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                document.map((doc, index) => (
                                    <tr key={`${doc.doc_id}-${index}`} className="align-middle">
                                        <td className="text-center p-3 text-muted fw-medium">
                                            {index + 1}
                                        </td>

                                        <td className="p-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div
                                                    className="flex-shrink-0 bg-light rounded-2 d-flex align-items-center justify-content-center"
                                                    style={{ width: "34px", height: "34px" }}
                                                >
                                                    <FileText
                                                        size={18}
                                                        className="text-primary"
                                                    />
                                                </div>

                                                <div className="min-w-0">
                                                    <a
                                                        href={doc.doc_file_path}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-none fw-semibold d-inline-flex align-items-center gap-1 text-primary"
                                                        title={`Open ${doc.doc_name}`}
                                                    >
                                                        <span className="text-break">
                                                            {doc.doc_name}
                                                        </span>

                                                        <ExternalLink
                                                            size={13}
                                                            className="opacity-75 flex-shrink-0"
                                                        />
                                                    </a>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            {doc.stage_display_name}
                                        </td>

                                        <td className="text-center p-3">
                                            <Badge
                                                bg="light"
                                                text="dark"
                                                className="text-uppercase px-3 py-2 fw-semibold"
                                            >
                                                {doc.doc_type}
                                            </Badge>
                                        </td>

                                        <td className="text-center p-3 text-nowrap text-gray-600">
                                            {CustomDateFormate(doc.creation_date ?? new Date())}
                                        </td>

                                        <td className="text-center p-3">
                                            {doc.can_delete ? (
                                                <Button
                                                    type="button"
                                                    variant="light-danger"
                                                    size="sm"
                                                    title="Delete"
                                                    disabled={isDeleting}
                                                    onClick={() => handleDeleteClick(doc)}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>

            <Suspense fallback={null}>
                <AddDocument
                    showModal={showModal}
                    setShowModal={setShowModal}
                    handleSave={handleSave}
                    categories={categories}
                />
            </Suspense>

            <DeleteConfirmationModal
                show={showDeleteModal}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                deleteMessage="Are you sure you want to delete this document? This action cannot be undone."
                popupLoader={isDeleting}
            />
        </div>
    );
};
